import * as React from 'react';
import { AgGridReact, AgGridReactProps } from 'ag-grid-react';
import glamorous from 'glamorous';
import { ColumnApi, GridApi, GridOptions } from 'ag-grid-community';
import LoadingOverlay from '../LoadingOverlay';
import classNames from 'classnames';
import withTranslate, { WithTranslateProps } from '../WithTranslate';
import { Tag } from '@blueprintjs/core';
import TranslatedMessage from '../translation/TranslatedMessage';
import { TLCode } from '../../../common/enums/TLCode';

// const XLSX = require('xlsx');
// const FileSaver = require('file-saver');

// tslint:disable-next-line:no-empty-interface
export interface AgGridContext extends WithTranslateProps {}

// tslint:disable-next-line:no-empty-interface
interface RenderGridProps extends GridOptions {}

// tslint:disable-next-line:no-any
type SafeGridApiInterface = (grid: GridApi) => any;
type SafeGridColumnApiInterface = (grid: ColumnApi) => any;

const AgGridContainer = glamorous.div({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
});

const HeaderContainer = glamorous.div({
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: 5,
});

const LeftContainer = glamorous.div({
  display: 'flex',
});

export interface BaseGridProps extends AgGridReactProps {
  className?: string;
  loading?: boolean;
  keyName: string;
  height?: string;
  isVisibleInDom?: boolean;
  children?: (props: {
    renderGrid: (props?: RenderGridProps) => React.ReactElement<AgGridReact>;
    callGridApi: (fn: SafeGridApiInterface) => any;
    callGridColumnApi: (fn: SafeGridColumnApiInterface) => any;
  }) => React.ReactNode;
  onRowDoubleClicked?: (value) => void;
}

interface BaseGridState {
  // gridReady: boolean;
  rowData: Array<{}>;
  rowCount: number;
}

class BaseGrid extends React.Component<BaseGridProps & WithTranslateProps, BaseGridState> {
  gridApi: GridApi;
  gridColumnApi: ColumnApi;

  state = {
    rowData: [{}],
    grid: null,
    rowCount: this.props.rowData ? this.props.rowData.length : 0,
  };

  componentDidUpdate(prevProps: BaseGridProps, prevState: BaseGridState) {
    if (prevProps.loading !== this.props.loading) {
      if (this.props.loading) {
        this.safeGridApi((gridApi) => gridApi.showLoadingOverlay());
      } else {
        this.safeGridApi((gridApi) => gridApi.hideOverlay());
      }
    }

    this.safeGridApi((gridApi) => gridApi.sizeColumnsToFit());
  }

  static getDerivedStateFromProps(nextProps: BaseGridProps, prevState: BaseGridState): Partial<BaseGridState> | null {
    if (nextProps.rowData! === prevState.rowData || nextProps.loading) {
      return null;
    }
    if (nextProps.rowData) {
      return {
        rowData: nextProps.rowData.map((e) => ({ ...e })),
      };
    } else {
      return {
        rowData: [{}],
      };
    }
  }

  getRowNodeId = (data: {}) => {
    const { keyName } = this.props;
    return data[keyName!];
  };

  onGridReady = (params) => {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    if (this.props.loading) {
      this.gridApi.showLoadingOverlay();
    }

    params.api.sizeColumnsToFit();

    window.onresize = () => {
      params.api.sizeColumnsToFit();
    };
  };

  safeGridColumnApi = (fn: SafeGridColumnApiInterface): any => {
    const gridColumnApi: ColumnApi = this.gridColumnApi;
    if (gridColumnApi) {
      return fn(gridColumnApi);
    }
  };

  safeGridApi = (fn: SafeGridApiInterface): any => {
    const gridApi: GridApi = this.gridApi;
    if (gridApi) {
      return fn(gridApi);
    }
  };

  get rowCount() {
    // return (this.props.rowData || []).length;
    // const gridApi: GridApi = this.gridApi;
    // if (gridApi) {
    //   return this.gridApi.getDisplayedRowCount();
    // }
    return this.state.rowCount;
  }

  render() {
    const defaultColDef = {
      editable: false,
      suppressDragLeaveHidesColumns: true,
      sortable: true,
      filter: true,
      resizable: true,
    };

    const { children, translate } = this.props;

    const rowCount = this.state.rowCount;

    const renderGrid = (props: RenderGridProps = {}) => {
      const context: AgGridContext = {
        translate,
      };

      return (
        <AgGridContainer>
          <AgGridReact
            defaultColDef={defaultColDef}
            deltaRowDataMode={true}
            // filter={true}
            // sortable={true}
            getRowNodeId={this.getRowNodeId}
            loadingOverlayComponentFramework={LoadingOverlay}
            onGridReady={this.onGridReady}
            rowSelection="multiple"
            suppressCellSelection={true}
            suppressMovableColumns={true}
            suppressNoRowsOverlay={true}
            context={context}
            onFilterChanged={(ev) => {
              this.setState({ rowCount: ev.api.getDisplayedRowCount() });
            }}
            {...this.props}
            {...props}
            rowData={this.state.rowData}
            onRowDoubleClicked={this.props.onRowDoubleClicked}
            gridOptions={{ enableBrowserTooltips: true }}
          />
        </AgGridContainer>
      );
    };

    return (
      <div
        className={classNames(this.props.className, 'ag-theme-balham').toString()}
        style={{
          backgroundColor: 'transparent',
          display: 'flex',
          flexDirection: 'column',
          height: this.props.height
            ? this.props.height == 'auto'
              ? 32 + (this.state.rowData!.length + 1) * 30 + 20 + 'px'
              : this.props.height
            : '100%',
        }}
      >
        <HeaderContainer>
          <LeftContainer>
            <Tag>
              <TranslatedMessage id={TLCode.GRID_NUMBER_RESULT} values={{ nbResult: rowCount }} />
            </Tag>
          </LeftContainer>
        </HeaderContainer>
        {children && typeof children === 'function'
          ? children({
              renderGrid,
              callGridApi: this.safeGridApi,
              callGridColumnApi: this.safeGridColumnApi,
            })
          : renderGrid()}
      </div>
    );
  }
}

export default withTranslate(BaseGrid);
